<template>
  <div class="materials-list-table-bulks-root relative w-full max-w-full min-w-0 overflow-x-auto border border-gray-300">
    <b-loading
      :active="isLoading"
      :is-full-page="false"
    />
    <table class="wpl-table w-full text-xs border-collapse">
      <thead>
        <tr>
          <th
            v-if="withBulk"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          ></th>
          <th
            v-if="isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            {{ $t('bulk_number') }}
          </th>
          <th
            v-if="isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            {{ $t('bulk_type') }}
          </th>
          <th
            v-if="isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            {{ $t('stackable') }}
          </th>
          <th
            v-if="isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            {{ $t('warehouse_type') }}
          </th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('category') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('type') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('order_number') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('provider') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('unique_material_id') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('description') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ isEntradasVista ? $t('quantity') : `${$t('quantity')}/Stock` }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('unit') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('storehouse') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('location') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('status') }}</th>
          <th class="px-2 py-1 text-left text-white border border-gray-700 bg-blue">{{ $t('comments') }}</th>
          <th
            v-if="!isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            {{ $t('irm_report') }}
          </th>
          <th
            v-if="!isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            {{ $t('id') }} {{ $t('activity') }}
          </th>
          <th
            v-if="!isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            {{ $t('drawing_number') }}
          </th>
          <th
            v-if="!isEntradasVista"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
          >
            KKS
          </th>
          <th
            v-if="depth === 0"
            class="px-2 py-1 text-left text-white border border-gray-700 bg-blue"
            style="width: 90px;"
          >
            {{ $t('actions') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!isLoading && displayRows.length === 0">
          <td :colspan="columnCount" class="px-2 py-3 text-center text-gray-500 border border-gray-300">{{ $t('no_results') }}</td>
        </tr>
      </tbody>

      <tbody
        v-for="(row, index) in displayRows"
        :key="row.id || row.unique_material_id || index"
      >
        <tr :class="rowClass(row)">
          <td
            v-if="withBulk"
            class="px-2 py-1 text-center border border-gray-300 expander-cell"
          >
            <button
              v-if="isBulk(row)"
              type="button"
              class="cursor-pointer"
              @click="toggleExpanded(row)"
            >
              <i class="fas" :class="isExpanded(row) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
            </button>
            <span v-else class="text-gray-400"></span>
          </td>
          <td v-if="isEntradasVista" class="px-2 py-1 border border-gray-300">{{ row.package_number || '-' }}</td>
          <td v-if="isEntradasVista" class="px-2 py-1 border border-gray-300">{{ row.package_type || '-' }}</td>
          <td v-if="isEntradasVista" class="px-2 py-1 border border-gray-300">{{ formatStackable(row.stackable) }}</td>
          <td v-if="isEntradasVista" class="px-2 py-1 border border-gray-300">{{ row.warehouse_type || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300">{{ row.category || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300">
            <span class="tag is-light" :class="isBulk(row) ? 'is-warning' : 'is-info'">
              {{ isBulk(row) ? $t('bulk') : $t('material') }}
            </span>
          </td>
          <td class="px-2 py-1 border border-gray-300">{{ row.order_number || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300 wpl-cell-wrap max-w-[8rem] sm:max-w-[10rem]">{{ row.provider || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300 wpl-cell-wrap max-w-[7rem] sm:max-w-[9rem]">{{ row.unique_material_id || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300 wpl-cell-wrap max-w-[12rem] sm:max-w-[18rem]">{{ row.description || '-' }}</td>
          <td class="px-2 py-1 text-right border border-gray-300">{{ formatQuantity(displayQuantity(row)) }}</td>
          <td class="px-2 py-1 border border-gray-300">{{ row.unit || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300">{{ row.storehouse || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300">{{ row.location || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300">{{ row.status || '-' }}</td>
          <td class="px-2 py-1 border border-gray-300 wpl-cell-wrap max-w-[10rem] sm:max-w-[14rem]">{{ row.comments || '-' }}</td>
          <td v-if="!isEntradasVista" class="px-2 py-1 border border-gray-300">{{ row.irm_report || '-' }}</td>
          <td v-if="!isEntradasVista" class="px-2 py-1 border border-gray-300">{{ row.activity_planning_code != null && String(row.activity_planning_code).trim() !== '' ? row.activity_planning_code : (row.activity_id != null ? row.activity_id : '-') }}</td>
          <td v-if="!isEntradasVista" class="px-2 py-1 border border-gray-300">{{ row.drawing_number || '-' }}</td>
          <td v-if="!isEntradasVista" class="px-2 py-1 border border-gray-300">{{ row.tag_kks || '-' }}</td>
          <td
            v-if="depth === 0"
            class="px-2 py-1 border border-gray-300"
          >
            <div v-if="withBulk && isBulk(row)" class="flex items-center gap-2">
              <button
                type="button"
                class="button is-small is-light"
                :title="$t('add')"
                @click="openAddMaterialToBulk(row)"
              >
                <span class="icon is-small">
                  <i class="fas fa-plus"></i>
                </span>
              </button>
              <button
                type="button"
                class="button is-small is-light"
                :title="$t('upload')"
                @click="openBulkImport(row)"
              >
                <span class="icon is-small">
                  <i class="fas fa-file-import"></i>
                </span>
              </button>
              <button
                type="button"
                class="button is-small is-light has-text-danger"
                :title="$t('delete')"
                :disabled="deletingInventoryId === row.id"
                @click="onDeleteInventory(row)"
              >
                <span class="icon is-small">
                  <i class="fas fa-trash-alt"></i>
                </span>
              </button>
            </div>
            <div v-else class="flex items-center gap-2">
              <button
                v-if="!isBulk(row)"
                type="button"
                class="button is-small is-light"
                :title="$t('edit')"
                @click="openEditMaterial(row)"
              >
                <span class="icon is-small">
                  <i class="fas fa-pencil-alt"></i>
                </span>
              </button>
              <button
                v-if="!isBulk(row)"
                type="button"
                class="button is-small is-light"
                :title="$t('warehouse_traceability_title')"
                @click="openTraceability(row)"
              >
                <span class="icon is-small">
                  <i class="fas fa-history"></i>
                </span>
              </button>
              <button
                v-if="!withBulk && shouldShowLinkAction(row)"
                type="button"
                class="px-2 py-1 text-blue-600 hover:text-blue-800"
                :title="$t('link')"
                @click="openAssignUniqueMaterial(row)"
              >
                <i class="fas fa-link" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                class="button is-small is-light has-text-danger"
                :title="$t('delete')"
                :disabled="deletingInventoryId === row.id"
                @click="onDeleteInventory(row)"
              >
                <span class="icon is-small">
                  <i class="fas fa-trash-alt"></i>
                </span>
              </button>
            </div>
          </td>
        </tr>
        <tr
          v-if="withBulk && isBulk(row) && isExpanded(row)"
          class="children-row"
        >
          <td :colspan="columnCount" class="p-0 border border-gray-300">
            <div
              class="child-wrapper max-w-full min-w-0 overflow-x-auto"
              :style="{ marginLeft: `${Math.min((depth + 1) * 16, 80)}px` }"
            >
              <materials-list-table-bulks
                :depth="depth + 1"
                :project-id="projectId"
                :packing-list-id="packingListId"
                :packing-list-number="packingListNumber"
                :parent-id="row.id"
                :cascade-refresh-key="childCascadeKey"
                :with-bulk="withBulk"
                :vista="vista"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
    <div
      v-if="depth === 0 && totalPages > 1"
      class="flex flex-wrap items-center justify-end gap-2 px-2 py-2 border-t border-gray-300 bg-white"
    >
      <button
        type="button"
        class="button is-small is-light"
        :disabled="currentPage <= 1 || isLoading"
        @click="prevPage"
      >
        <span class="icon is-small">
          <i class="fas fa-chevron-left"></i>
        </span>
      </button>
      <span class="text-xs text-gray-600">
        {{ currentPage }} / {{ totalPages }}
      </span>
      <button
        type="button"
        class="button is-small is-light"
        :disabled="currentPage >= totalPages || isLoading"
        @click="nextPage"
      >
        <span class="icon is-small">
          <i class="fas fa-chevron-right"></i>
        </span>
      </button>
    </div>

    <warehouse-add-inventory-material-modal
      v-if="depth === 0 && withBulk"
      v-model="isAddMaterialModalActive"
      :project-id="projectId"
      :parent-inventory-id="addModalParentInventoryId"
      :packing-list-id="packingListId"
      :preset-packing-list-number="packingListNumber"
      @saved="onAddMaterialModalSaved"
    />

    <warehouse-add-inventory-material-modal
      v-if="depth === 0"
      v-model="isEditMaterialModalActive"
      :project-id="projectId"
      :is-editing-material="true"
      :edit-inventory-id="editInventoryId"
      :packing-list-id="packingListId"
      :preset-packing-list-number="packingListNumber"
      @saved="onEditMaterialModalSaved"
    />

    <b-modal
      v-if="depth === 0"
      :active="isAssignUniqueMaterialModalActive"
      has-modal-card
      trap-focus
      @close="closeAssignUniqueMaterialModal"
    >
      <div class="modal-card" style="width: 520px; max-width: 98%;">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ $t('material') }}</p>
          <button type="button" class="delete" @click="closeAssignUniqueMaterialModal" />
        </header>
        <section class="modal-card-body">
          <b-loading :active="isAssignUniqueMaterialLoading" :is-full-page="false" />
          <p class="mb-3 text-sm text-gray-600">
            asigne un material unico al item
          </p>
          <b-field :label="$t('material')">
            <b-select v-model="assignUniqueMaterialId" expanded>
              <option :value="null">-</option>
              <option v-for="m in assignUniqueMaterialOptions" :key="`aum-${m.id}`" :value="m.id">
                {{ (m.code ? `${m.code} - ` : '') + (m.description || `#${m.id}`) }}
              </option>
            </b-select>
          </b-field>
        </section>
        <footer class="modal-card-foot is-justify-content-flex-end">
          <button type="button" class="button is-light" @click="closeAssignUniqueMaterialModal">
            {{ $t('cancel') }}
          </button>
          <button
            type="button"
            class="button is-primary"
            :class="{ 'is-loading': isAssignUniqueMaterialSaving }"
            :disabled="!assignUniqueMaterialId || isAssignUniqueMaterialSaving"
            @click="saveAssignUniqueMaterial"
          >
            {{ $t('save') }}
          </button>
        </footer>
      </div>
    </b-modal>

    <warehouse-import-packing-list-modal
      v-if="depth === 0 && withBulk"
      v-model="isBulkImportModalActive"
      :project-id="projectId"
      :import-entity-type="importEntityType"
      :extra-payload="bulkImportExtraPayload"
      @success="onBulkImportModalSuccess"
    />

    <warehouse-inventory-traceability-modal
      v-if="depth === 0"
      v-model="isTraceabilityModalActive"
      :project-id="projectId"
      :inventory-id="traceabilityInventoryId"
      :header-info="traceabilityHeaderInfo"
    />
  </div>
</template>

<script>
import axios from 'axios'
import WarehouseAddInventoryMaterialModal from './warehouse-add-inventory-material-modal.vue'
import WarehouseImportPackingListModal from './warehouse-import-packing-list-modal.vue'
import WarehouseInventoryTraceabilityModal from './warehouse-inventory-traceability-modal.vue'

export default {
  name: 'materials-list-table-bulks',
  components: {
    WarehouseAddInventoryMaterialModal,
    WarehouseImportPackingListModal,
    WarehouseInventoryTraceabilityModal,
  },
  props: {
    projectId: { type: [Number, String], required: true},
    /**
     * true: vista packing list (filtra por warehouse_packing_list_id, acciones import/añadir).
     * false: inventario del proyecto completo (sin filtro de packing list en API).
     */
    withBulk: { type: Boolean, default: true },
    packingListId: { type: [Number, String], required: false, default: null },
    /** Filtro opcional para modo sin bultos: solo líneas de este material de catálogo. */
    warehouseMaterialId: { type: [Number, String], required: false, default: null },
    /** Filtro opcional para modo sin bultos: solo líneas sin material de catálogo vinculado. */
    withoutWarehouseMaterial: { type: Boolean, default: false },
    /**
     * Filtros extra para modo sin bultos (inventario plano).
     * Ej:
     * {
     *   warehouse_material_id: number|null,
     *   warehouse_provider_id: number|null,
     *   unit_id: number|null,
     *   search: string
     * }
     */
    filters: { type: Object, default: () => ({}) },
    /** Número de cabecera del packing list (p. ej. desde la fila del b-table) para precargar el formulario al agregar bajo un bulto. */
    packingListNumber: { type: String, default: ''},
    /** Raíz: null o omitido → API parent_id=0. Anidado: id del bulto padre. */
    parentId: { type: [Number, String], required: false, default: null},
    depth: { type: Number, default: 0},
    perPage: { type: Number, default: 25},
    /**
     * Contador que propagan los ancestros cuando hay que refrescar hijos (mismo packing list).
     * La raíz (depth 0) emite incrementos locales al guardar material en un bulto.
     */
    cascadeRefreshKey: { type: Number, default: 0},
    importEntityType: { type: String, default: 'warehouse_packing_list' },
    /** null: todas las columnas. 'entradas': oculta IRM, actividad, plano y KKS. */
    vista: { type: String, default: null },
  },
  data() {
    return {
      expandedRows: {},
      isLoading: false,
      rows: [],
      localCurrentPage: 1,
      paginationMeta: {
        lastPage: 1,
        total: 0,
        perPage: 25,
      },
      localCascadeRefreshTick: 0,
      isAddMaterialModalActive: false,
      addModalParentInventoryId: null,
      isEditMaterialModalActive: false,
      editInventoryId: null,
      isAssignUniqueMaterialModalActive: false,
      isAssignUniqueMaterialLoading: false,
      isAssignUniqueMaterialSaving: false,
      assignUniqueMaterialInventoryId: null,
      assignUniqueMaterialId: null,
      assignUniqueMaterialOptions: [],
      isBulkImportModalActive: false,
      bulkImportExtraPayload: {},
      isTraceabilityModalActive: false,
      traceabilityInventoryId: null,
      traceabilityHeaderInfo: {},
      deletingInventoryId: null,
    }
  },
  computed: {
    isEntradasVista() {
      return this.vista === 'entradas'
    },
    columnCount() {
      const expander = this.withBulk ? 1 : 0
      const actions = this.depth === 0 ? 1 : 0
      const entradasExtra = this.isEntradasVista ? 4 : 0
      const entradasHidden = this.isEntradasVista ? 4 : 0
      return 16 + expander + actions + entradasExtra - entradasHidden
    },
    displayRows() {
      return this.rows || []
    },
    currentPage() {
      if (this.depth !== 0) return 1
      return this.localCurrentPage
    },
    totalPages() {
      if (this.depth !== 0) return 1
      const last = Number(this.paginationMeta.lastPage)
      return last > 0 ? last : 1
    },
    apiParentId() {
      if (this.parentId == null || this.parentId === '') return 0
      const n = Number(this.parentId)
      return Number.isFinite(n) && n > 0 ? n : 0
    },
    /** Valor hijos anidados deben observar (raíz = tick local; niveles inferiores = prop). */
    childCascadeKey() {
      return this.depth === 0 ? this.localCascadeRefreshTick : this.cascadeRefreshKey
    },
    normalizedFiltersKey() {
      // stringify para que el watch reaccione a cambios internos del objeto filters
      if (!this.filters || typeof this.filters !== 'object') return ''
      try {
        return JSON.stringify(this.filters)
      } catch (_) {
        return ''
      }
    },
  },
  watch: {
    projectId: 'onScopeChanged',
    packingListId: 'onScopeChanged',
    withBulk: 'onScopeChanged',
    warehouseMaterialId: 'onScopeChanged',
    withoutWarehouseMaterial: 'onScopeChanged',
    normalizedFiltersKey: 'onScopeChanged',
    parentId: 'onScopeChanged',
    cascadeRefreshKey(newVal, oldVal) {
      if (this.depth === 0) {
        return
      }
      if (oldVal === undefined) {
        return
      }
      if (newVal !== oldVal) {
        this.fetchInventory()
      }
    },
    isAddMaterialModalActive(open) {
      if (!open && this.depth === 0) {
        this.addModalParentInventoryId = null
      }
    },
    isBulkImportModalActive(open) {
      if (!open && this.depth === 0) {
        this.bulkImportExtraPayload = {}
      }
    },
    isEditMaterialModalActive(open) {
      if (!open && this.depth === 0) {
        this.editInventoryId = null
      }
    },
    isAssignUniqueMaterialModalActive(open) {
      if (!open && this.depth === 0) {
        this.assignUniqueMaterialInventoryId = null
        this.assignUniqueMaterialId = null
      }
    },
    isTraceabilityModalActive(open) {
      if (!open && this.depth === 0) {
        this.traceabilityInventoryId = null
        this.traceabilityHeaderInfo = {}
      }
    },
  },
  created() {
    this.fetchInventory()
  },
  methods: {
    onScopeChanged() {
      this.localCurrentPage = 1
      this.fetchInventory()
    },
    isBulk(row) {
      return String(row && row.entry_type || '').toUpperCase() === 'B'
    },
    isExpanded(row) {
      return Boolean(this.expandedRows[row.id])
    },
    shouldShowLinkAction(row) {
      const value = row && row.unique_material_id
      return value == null || String(value).trim() === ''
    },
    toggleExpanded(row) {
      const id = row && row.id ? row.id : null
      if (id == null) return
      const nextValue = !this.expandedRows[id]
      this.expandedRows = { ...this.expandedRows, [id]: nextValue }
    },
    prevPage() {
      if (this.depth !== 0 || this.localCurrentPage <= 1) return
      this.localCurrentPage -= 1
      this.fetchInventory()
    },
    nextPage() {
      if (this.depth !== 0 || this.localCurrentPage >= this.totalPages) return
      this.localCurrentPage += 1
      this.fetchInventory()
    },
    async fetchInventory() {
      const projectId = this.projectId != null ? Number(this.projectId) : null
      const packingListId = this.packingListId != null && this.packingListId !== ''
        ? Number(this.packingListId)
        : null
      if (!projectId) {
        this.rows = []
        return
      }
      if (this.withBulk && (!Number.isFinite(packingListId) || packingListId <= 0)) {
        this.rows = []
        return
      }

      const page = this.depth === 0 ? this.localCurrentPage : 1
      const perPage = this.depth === 0 ? this.perPage : 50

      this.isLoading = true
      try {
        const params = {
          per_page: perPage,
          page,
          order_field: 'id',
          order_dir: 'desc',
        }
        if (this.isEntradasVista) {
          // En la vista de Entradas la columna "Cantidad" debe reflejar las unidades recibidas
          // originalmente, no el stock vivo (que se decrementa con salidas y se incrementa con devoluciones).
          params.with_received_totals = 1
        }
        if (this.withBulk) {
          params.warehouse_packing_list_id = packingListId
          params.parent_id = this.apiParentId
        } else {
          params.with_bulk = 0
          const extraFilters = this.filters && typeof this.filters === 'object' ? this.filters : {}
          const fMat = extraFilters.warehouse_material_id != null && extraFilters.warehouse_material_id !== ''
            ? Number(extraFilters.warehouse_material_id)
            : null
          const fProvider = extraFilters.warehouse_provider_id != null && extraFilters.warehouse_provider_id !== ''
            ? Number(extraFilters.warehouse_provider_id)
            : null
          const fUnit = extraFilters.unit_id != null && extraFilters.unit_id !== ''
            ? Number(extraFilters.unit_id)
            : null
          const fActivity = extraFilters.material_activity_id != null && extraFilters.material_activity_id !== ''
            ? Number(extraFilters.material_activity_id)
            : null
          const fMaterialOrderNumber = extraFilters.material_order_number != null
            ? String(extraFilters.material_order_number).trim()
            : ''
          const fSearch = extraFilters.search != null ? String(extraFilters.search).trim() : ''

          if (this.withoutWarehouseMaterial) {
            params.without_warehouse_material = 1
          } else {
            const warehouseMaterialId = this.warehouseMaterialId != null && this.warehouseMaterialId !== ''
              ? Number(this.warehouseMaterialId)
              : null
            if (warehouseMaterialId > 0) {
              params.warehouse_material_id = warehouseMaterialId
            }
          }

          if (Number.isFinite(fMat) && fMat > 0) {
            params.warehouse_material_id = fMat
          }
          if (Number.isFinite(fProvider) && fProvider > 0) {
            params.warehouse_provider_id = fProvider
          }
          if (Number.isFinite(fUnit) && fUnit > 0) {
            params.unit_id = fUnit
          }
          if (Number.isFinite(fActivity) && fActivity > 0) {
            params.material_activity_id = fActivity
          }
          if (fMaterialOrderNumber !== '') {
            params.material_order_number = fMaterialOrderNumber
          }
          if (fSearch !== '') {
            // El backend usa `description` como filtro de texto libre
            params.description = fSearch
          }
        }
        const { data } = await axios.get(`/api/v2/project/${projectId}/warehouse/inventory`, {
          params,
        })
        this.rows = this.extractArrayFromResponse(data, 'inventory')
        if (this.depth === 0) {
          const cp = Number(data && data.currentPage)
          if (cp > 0) {
            this.localCurrentPage = cp
          }
          this.paginationMeta = {
            lastPage: Number(data && data.lastPage) || 1,
            total: Number(data && data.total) || 0,
            perPage: Number(data && data.perPage) || perPage,
          }
        }
      } catch (e) {
        this.rows = []
        if (this.depth === 0) {
          this.paginationMeta = {
            lastPage: 1,
            total: 0,
            perPage: this.perPage,
          }
        }
      } finally {
        this.isLoading = false
      }
    },
    extractArrayFromResponse(data, key) {
      if (Array.isArray(data && data[key])) return data[key]
      if (Array.isArray(data && data.data && data.data[key])) return data.data[key]
      return []
    },
    rowClass(row) {
      if (this.isBulk(row)) return 'bulk-row'
      if (this.depth > 0) return 'child-material-row'
      return ''
    },
    formatQuantity(value) {
      if (value == null || Number.isNaN(Number(value))) return '-'
      return Number(value).toLocaleString('es-ES', { maximumFractionDigits: 2 })
    },
    displayQuantity(row) {
      if (this.isEntradasVista && row && row.original_received_quantity != null) {
        return row.original_received_quantity
      }
      return row && row.quantity
    },
    formatStackable(value) {
      if (value == null || value === '') return '-'
      return Number(value) === 1 ? 'YES' : 'NO'
    },
    openAddMaterialToBulk(row) {
      if (!row || !row.id) return
      this.addModalParentInventoryId = Number(row.id)
      this.isAddMaterialModalActive = true
    },
    async onAddMaterialModalSaved() {
      this.addModalParentInventoryId = null
      if (this.depth === 0) {
        this.localCascadeRefreshTick += 1
      }
      await this.fetchInventory()
    },
    openBulkImport(row) {
      if (!row || !row.id || this.depth !== 0) return
      this.bulkImportExtraPayload = {
        parent_id: Number(row.id),
      }
      this.isBulkImportModalActive = true
    },
    openEditMaterial(row) {
      if (!row || !row.id || this.depth !== 0) return
      if (this.isBulk(row)) return
      this.editInventoryId = Number(row.id)
      this.isEditMaterialModalActive = true
    },
    openTraceability(row) {
      if (!row || !row.id || this.depth !== 0) return
      if (this.isBulk(row)) return
      this.traceabilityInventoryId = Number(row.id)
      this.traceabilityHeaderInfo = {
        unique_material_id: row.unique_material_id || '',
        description: row.description || '',
        packing_list_number: row.packing_list_number || '',
      }
      this.isTraceabilityModalActive = true
    },
    async onEditMaterialModalSaved() {
      this.editInventoryId = null
      await this.fetchInventory()
    },
    async openAssignUniqueMaterial(row) {
      if (!row || !row.id || this.depth !== 0) return
      if (this.isBulk(row)) return
      if (this.withBulk) return
      if (!this.shouldShowLinkAction(row)) return

      this.assignUniqueMaterialInventoryId = Number(row.id)
      this.assignUniqueMaterialId = null
      this.isAssignUniqueMaterialModalActive = true

      if (Array.isArray(this.assignUniqueMaterialOptions) && this.assignUniqueMaterialOptions.length > 0) {
        return
      }

      const projectId = this.projectId != null ? Number(this.projectId) : null
      if (!projectId) return

      this.isAssignUniqueMaterialLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${projectId}/warehouse/materials`, {
          params: { per_page: 100, page: 1, order_field: 'code', order_dir: 'asc' },
        })
        const list = this.extractArrayFromResponse(data, 'materials')
        this.assignUniqueMaterialOptions = Array.isArray(list) ? list : []
      } catch (e) {
        this.assignUniqueMaterialOptions = []
      } finally {
        this.isAssignUniqueMaterialLoading = false
      }
    },
    closeAssignUniqueMaterialModal() {
      this.isAssignUniqueMaterialModalActive = false
    },
    async saveAssignUniqueMaterial() {
      const projectId = this.projectId != null ? Number(this.projectId) : null
      const inventoryId = this.assignUniqueMaterialInventoryId != null ? Number(this.assignUniqueMaterialInventoryId) : null
      const materialId = this.assignUniqueMaterialId != null ? Number(this.assignUniqueMaterialId) : null
      if (!projectId || !inventoryId || !materialId) return

      this.isAssignUniqueMaterialSaving = true
      try {
        await axios.put(
          `/api/v2/project/${projectId}/warehouse/inventory/${inventoryId}/material`,
          { warehouse_material_id: materialId },
        )
        if (this.$notify) {
          this.$notify.success('save_successful')
        }
        this.isAssignUniqueMaterialModalActive = false
        await this.fetchInventory()
      } catch (e) {
        if (this.$notify) {
          this.$notify.error('save_error')
        }
      } finally {
        this.isAssignUniqueMaterialSaving = false
      }
    },
    async onBulkImportModalSuccess() {
      if (this.depth === 0) {
        this.localCascadeRefreshTick += 1
      }
      await this.fetchInventory()
      this.$emit('import-success')
    },
    firstValidationMessage(errorData) {
      const err = errorData && errorData.errors
      if (err && typeof err === 'object') {
        const firstKey = Object.keys(err)[0]
        if (firstKey && Array.isArray(err[firstKey]) && err[firstKey].length) {
          return err[firstKey][0]
        }
      }
      if (errorData && errorData.message) {
        return errorData.message
      }
      return null
    },
    onDeleteInventory(row) {
      if (!row || row.id == null) return
      const projectId = this.projectId != null ? Number(this.projectId) : null
      if (!projectId) return
      const inventoryId = Number(row.id)
      const label = row.unique_material_id || row.description || String(inventoryId)
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_inventory_line_confirm_text', [label]),
        confirmText: this.$t('delete'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          this.deletingInventoryId = inventoryId
          try {
            const { data } = await axios.delete(
              `/api/v2/project/${projectId}/warehouse/inventory/${inventoryId}`
            )
            if (data && data.success) {
              this.$buefy.toast.open({
                message: this.$t('deleted_successfully'),
                type: 'is-success',
                position: 'is-top-right',
              })
              if (this.depth === 0) {
                this.localCascadeRefreshTick += 1
              }
              await this.fetchInventory()
              this.$emit('deleted')
            } else {
              const msg = this.firstValidationMessage(data) || this.$t('error')
              this.$buefy.toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
            }
          } catch (e) {
            const resp = e.response && e.response.data
            const msg = this.firstValidationMessage(resp) || (e.message ? String(e.message) : '') || this.$t('error')
            console.error('[materials-list-table-bulks] delete failed', { status: e.response && e.response.status, response: resp, error: e })
            this.$buefy.toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
          } finally {
            this.deletingInventoryId = null
          }
        },
      })
    },
  },
}
</script>

<style scoped>
.materials-list-table-bulks-root {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
}
.wpl-table {
  border: 1px solid #9ca3af;
  table-layout: auto;
  min-width: 1300px;
}
.wpl-cell-wrap {
  overflow-wrap: anywhere;
  word-break: break-word;
  vertical-align: top;
}
.wpl-table th,
.wpl-table td {
  border: 1px solid #d1d5db;
}
.wpl-table thead th {
  border-color: #9ca3af;
}
.children-row td {
  background-color: #f9fafb;
}
.child-wrapper {
  border-left: 3px solid #d1d5db;
}
.bulk-row {
  background-color: #fff8e1;
}
.child-material-row {
  background-color: #f8fbff;
}
.expander-cell {
  width: 34px;
  min-width: 34px;
}
</style>
